import { createSelector } from '@reduxjs/toolkit';
import { Pax } from 'gql/autogen/Pax';
import { setOfferNotification, setPassengers } from 'component/page/detail/store/availabilityIndex';
import {
    selectActivityIdFromSelectedOffer,
    selectAvailabilityQuantitySelectedOffer,
    selectAvailabilitySearchCriteriaStatus,
    selectFinalOfferOption,
    selectOfferLevelPaxRequirements,
    selectOfferLevelSelectedDate,
    selectPassengerOption,
    selectPossibleOfferOptions,
    selectSelectedBookingRequirement,
    selectTotalMaxPax,
    selectTotalPax,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { selectCmsFromBootstrap, selectExperimentsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { CmsProps } from 'core/type/BootstrapConfig.types';
import { PaxTypeCode } from 'gql/autogen/PaxTypeCode';
import { EnrichedBookingRequirement } from 'component/page/detail/store/availabilityIndex.types';
import { AvailabilitySearchCriteriaStatusErrorMapper } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardBookButtonSection/OfferCardFooter.selectors';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { PaxRequirement } from 'gql/autogen/PaxRequirement';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';
import { getMinPassengerConstraint } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferCardPaxPanel/PaxCounter/paxUtils';

export type PaxPanelProps = PaxPanelOwnProps & PaxPanelStateProps & PaxPanelDispatchProps;

interface PaxPanelOwnProps {
    inProgress: boolean;
}

interface PaxPanelStateProps extends CmsProps {
    paxState: Pax[];
    selectedBookingRequirement: EnrichedBookingRequirement;
    isTicketTitle: boolean;
    selectedDate: Date;
    errorMessage: string;
    offerPaxRequirement: PaxRequirement[];
    possibleOfferOptions: EnrichedOfferOption[];
    searchCriteriaStatus: string;
    availableQuantity: number;
    isAllOfferSpecSelected: boolean;
    isTotalReachedMaxPax: boolean;
    minPassengerMessage: string;
    activityId: number | undefined;
    isUpdateAvailabilitySlotAlert: boolean;
}

interface PaxPanelDispatchProps {
    setPassengers: (passengers: Pax[]) => void;
    setOfferNotification: (offerNotification: Record<string, boolean>) => void;
}

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectExperimentsFromBootstrap,
    selectSelectedBookingRequirement,
    selectPassengerOption,
    selectOfferLevelPaxRequirements,
    selectPossibleOfferOptions,
    selectOfferLevelSelectedDate,
    selectAvailabilitySearchCriteriaStatus,
    selectAvailabilityQuantitySelectedOffer,
    selectFinalOfferOption,
    selectTotalPax,
    selectTotalMaxPax,
    selectActivityIdFromSelectedOffer,
    selectFeatureBehaviorFromBootstrap,
    (
        cms,
        experiments,
        selectedBookingRequirement,
        paxState,
        offerPaxRequirement,
        possibleOfferOptions,
        selectedDate,
        searchCriteriaStatus,
        availableQuantity,
        isAllOfferSpecSelected,
        totalPax,
        totalMaxPax,
        activityId,
        featureBehavior,
    ): PaxPanelStateProps => {
        const isAct4603 = isB(ExperimentId.ACT_4603, experiments);
        const isUpdateAvailabilitySlotAlert = featureBehavior.UpdateAvailabilitySlotAlert;
        const isTicketTitle = (selectedBookingRequirement.paxRequirements || []).some((paxRequirements) => paxRequirements.code === PaxTypeCode.TICKET);
        const availabilitySearchCriteriaStatusMapper = AvailabilitySearchCriteriaStatusErrorMapper[searchCriteriaStatus];
        const errorMessage = availabilitySearchCriteriaStatusMapper(cms, selectedBookingRequirement, isAct4603, isUpdateAvailabilitySlotAlert);
        const minPassengerMessage = getMinPassengerConstraint(cms, selectedBookingRequirement, isAct4603);
        return {
            selectedBookingRequirement,
            cms,
            paxState,
            offerPaxRequirement,
            possibleOfferOptions,
            isTicketTitle,
            selectedDate,
            errorMessage,
            searchCriteriaStatus,
            availableQuantity,
            minPassengerMessage,
            isTotalReachedMaxPax: totalPax >= totalMaxPax,
            isAllOfferSpecSelected: !!isAllOfferSpecSelected,
            activityId,
            isUpdateAvailabilitySlotAlert,
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): PaxPanelDispatchProps => ({
    setPassengers: (passengers: Pax[]) => {
        const validPassengers = passengers.filter((pax) => pax.quantity.value > 0);
        dispatch(setPassengers(validPassengers));
    },
    setOfferNotification: (offerNotification: Record<string, boolean>) => {
        dispatch(setOfferNotification(offerNotification));
    },
});
